import type { ExtractedLeadV2 } from "../../../types/lead";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { ghostBtnClass } from "@/components/common/surfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useResolveCrmLinkUrl } from "@/hooks/useResolveCrmLinkUrl";
import { speak } from "../../../lib/speech/speak";
import { getSpeakMode } from "../../../lib/speech/voiceSettings";
import type { LeadLike } from "./LeadSummary";

type LeadSummaryActionsProps = {
  currentLead: LeadLike;
  ghlPayload: any | null;
  validationIssues: string[];
  canSend: boolean;
  isSending: boolean;
  handleSendClick: (lead: LeadLike) => void;
  reextractCurrentLead: () => void;
  devMode: boolean;
};

export function LeadSummaryActions({
  currentLead,
  ghlPayload,
  validationIssues,
  canSend,
  isSending,
  handleSendClick,
  reextractCurrentLead,
  devMode,
}: LeadSummaryActionsProps) {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const resolveCrmLinkUrl = useResolveCrmLinkUrl(scopedOrgId, auth.activeLocationId);
  const crmLinkUrl =
    !currentLead.isTest && currentLead.sent ? resolveCrmLinkUrl(currentLead) : null;

  return (
    <>
      <button
        {...{ [FIREFLY_TARGET_ATTR]: "send-lead" }}
        onClick={() => {
          if (currentLead.isTest) return;
          if (!canSend) {
            const speakMode = getSpeakMode();
            if (speakMode === "help" || speakMode === "all") {
              speak("I can't send this lead yet. Please make sure you have a name and a valid phone number.");
            }
            return;
          }
          if (!currentLead.sent && !isSending) {
            handleSendClick(currentLead);
          }
        }}
        disabled={!canSend || currentLead.sent || isSending}
        className={`mt-8 w-full py-6 text-3xl rounded-2xl ${
          currentLead.isTest
            ? "bg-gray-500"
            : currentLead.sent
            ? "bg-gray-500"
            : !canSend
            ? "bg-gray-400"
            : isSending
            ? "bg-red-500"
            : "bg-red-600 hover:bg-red-700"
        } text-white font-bold`}
      >
        {currentLead.isTest
          ? "Test lead - will not send"
          : currentLead.sent
          ? "Sent to CRM"
          : !canSend
          ? "Fix lead details before sending"
          : isSending
          ? "Sending to CRM..."
          : "Send to CRM"}
      </button>

      {!currentLead.isTest ? (
        <div
          {...{ [FIREFLY_TARGET_ATTR]: "lead-crm-link" }}
          className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-xs dark:border-emerald-800/60 dark:bg-emerald-950/30"
        >
          {crmLinkUrl ? (
            <a
              href={crmLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-900 dark:text-emerald-200 dark:hover:text-emerald-100"
            >
              Open in CRM
            </a>
          ) : (
            <p className="text-emerald-900 dark:text-emerald-100">
              After a successful send, look for <strong>Open in CRM</strong> in the success toast
              {currentLead.sent ? " and here" : ""} when your org has a CRM URL in Location Tools.
            </p>
          )}
        </div>
      ) : null}

      {currentLead.sendError && !currentLead.isTest && (
        <p className="mt-2 text-sm text-red-600">Last send error: {currentLead.sendError}</p>
      )}

      {currentLead.queued && !currentLead.sent && !currentLead.isTest && (
        <p className="mt-1 text-xs text-amber-700">
          This lead is queued and will retry automatically when the device is online.
        </p>
      )}

      <button
        {...{ [FIREFLY_TARGET_ATTR]: "lead-rerun-extract" }}
        onClick={reextractCurrentLead}
        className={`mt-2 w-full py-2 text-sm rounded-2xl ${ghostBtnClass}`}
      >
        Re-run extraction from transcript
      </button>

      {!currentLead.isTest && validationIssues.length > 0 && (
        <div className="mt-3 text-xs text-red-600">
          <p className="font-semibold mb-1">Cannot send yet. Please fix the following:</p>
          <ul className="list-disc list-inside space-y-1">
            {validationIssues.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {!canSend && !currentLead.isTest && (
        <p className="mt-1 text-xs text-red-600">
          Cannot send yet - make sure you've captured at least a name and phone number.
        </p>
      )}

      {devMode && (
        <div className="mt-4 space-y-2">
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer">Show raw extraction JSON</summary>
            <pre className="mt-2 text-[10px] bg-black text-green-400 p-3 rounded-xl overflow-x-auto">
              {JSON.stringify(currentLead.extracted as ExtractedLeadV2 | null, null, 2)}
            </pre>
          </details>

          {ghlPayload && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">Show CRM payload (preview)</summary>
              <pre className="mt-2 text-[10px] bg-black text-blue-200 p-3 rounded-xl overflow-x-auto">
                {JSON.stringify(ghlPayload, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </>
  );
}
